import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useStores } from '../../../../cls/stores/MobXProvider'
import { userHasUpdates } from '../../../../stores/ui-lst-users/actions'
import { actLoadUser } from '../../../../stores/users/actions'
import { actLoadSprints } from '../../../../stores/ui-lst-sprints/actions'
import { actLoadRequests } from '../../../../stores/ui-lst-requests/actions'
import { useNavigate } from 'react-router-dom'
/**
 * Modified
 *
 * A background component that periodically checks if the logged-in user's data
 * or related entities have been updated on the server. If updates are detected,
 * it refreshes the user or related data in the MobX store accordingly.
 *
 * It specifically:
 * - Updates user data if the modification timestamp changes.
 * - Updates related data (like sprints) if the related modification timestamp changes.
 * - Avoids redundant requests when on the user edit page.
 * - Triggers updates conditionally based on pathname (e.g., reloads sprints if on `/sprints`).
 *
 * @component
 * @returns {null} This component performs side effects only and does not render any JSX.
 */
const Modified = () => {
	const navigate = useNavigate()
	const { users, modifiedFrequency } = useStores()
	const { loggedInUser, loggedInUserId } = users

	const prevModifiedRef = useRef(null)
	const prevModifiedRelatedRef = useRef(null)
	const hasInitialized = useRef(false)

	useEffect(() => {
		if (!loggedInUserId) return

		const interval = setInterval(async () => {
			try {
				const response = await userHasUpdates()
				if (!response) return
				let shouldUpdateUser = false
				let shouldUpdateRelated = false

				const { modified: newModified, modified_related: newRelated } =
					response

				if (!hasInitialized.current) {
					// First time: just store and skip
					prevModifiedRef.current = newModified
					prevModifiedRelatedRef.current = newRelated
					hasInitialized.current = true
					return
				}

				if (
					loggedInUser.modifiedTs !== newModified &&
					prevModifiedRef.current !== newModified
				) {
					console.log('User has updates:', {
						old: loggedInUser.modifiedTs,
						new: newModified
					})
					shouldUpdateUser = true
					loggedInUser.setTrackModified(newModified)
					prevModifiedRef.current = newModified
				}

				if (
					loggedInUser.modifiedRelatedTs !== newRelated &&
					prevModifiedRelatedRef.current !== newRelated
				) {
					console.log('User has related updates:', {
						old: loggedInUser.modifiedRelatedTs,
						new: newRelated
					})
					shouldUpdateRelated = true
					loggedInUser.setTrackModifiedRelated(newRelated)
					prevModifiedRelatedRef.current = newRelated
				}

				const urlPath = window.location.pathname
				if (shouldUpdateRelated) {
					shouldUpdateRelated = false

					if (urlPath.endsWith('/sprints')) {
						// Trigger the sprints request
						await actLoadSprints().catch(console.error)
					} else if (urlPath.endsWith('/requests')) {
						// Trigger the requests request
						await actLoadRequests().catch(console.error)
					}
				}
				if (shouldUpdateUser) {
					shouldUpdateUser = false

					// We need to check if we are on user edit page, if we are, we will not load the user as it's already loaded by another process
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const isOnUserEditPage = urlPath.includes(
						`/users/${loggedInUserId}/edit`
					)

					// if (!isOnUserEditPage) {
					// Trigger the user request
					actLoadUser(users, loggedInUserId, true).then(() => {
						users.saveState()
					})
					// }
				}
			} catch (error) {
				console.error('Error checking user updates:', error)
				if (error?.status === 'error' && error?.action === 'logout') {
					if (error.reason) {
						// add reson as parameter to /login?reason=...
						navigate(
							`/login?reason=${encodeURIComponent(error.reason)}`,
							{ replace: true }
						)
					} else {
						navigate('/login', { replace: true })
					}
				}
			}
		}, modifiedFrequency * 1000)

		return () => clearInterval(interval)
	}, [loggedInUserId, modifiedFrequency])

	return null
}

export default observer(Modified)
